import { useState } from 'react';
import { TextField, InputAdornment, type TextFieldProps } from '@mui/material';
import { useTranslation } from 'react-i18next';

export type WorkingTimeFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  name: string;
  value: number | null | undefined | string;
  onChange: (event: { target: { name: string; value: number | null } }) => void;
};

const format = (value: number | null) => (value == null ? '' : String(Math.round(value * 10000) / 100));

export const WorkingTimeField = ({
  name,
  value,
  onChange,
  InputProps,
  inputProps,
  type: _type,
  helperText,
  ...rest
}: WorkingTimeFieldProps) => {
  const { t } = useTranslation(['form']);
  const normalizedValue = value === '' || value == null ? null : Number(value);

  const [inputValue, setInputValue] = useState(() => format(normalizedValue));
  const [prevValue, setPrevValue] = useState(normalizedValue);
  if (normalizedValue !== prevValue) {
    setPrevValue(normalizedValue);
    setInputValue(format(normalizedValue));
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (raw === '') {
      setInputValue('');
      onChange({ target: { name, value: null } });
      return;
    }
    // Allow at most 3 digits (max is 100%)
    const digitCount = raw.replace(/[^0-9]/g, '').length;
    if (digitCount > 3) return;
    const numValue = Number(raw);
    if (isNaN(numValue) || numValue <= 0 || numValue > 100) return;
    setInputValue(raw);
    const percentage = Math.round((numValue / 100) * 10000) / 10000;
    onChange({ target: { name, value: percentage } });
  };

  const derivedHours =
    normalizedValue != null ? t('field.working time hours per week', { hours: (normalizedValue * 40).toFixed(2) }) : '';

  return (
    <TextField
      {...rest}
      fullWidth
      name={name}
      value={inputValue}
      onChange={handleChange}
      type="number"
      inputProps={{ min: 1, max: 100, ...inputProps }}
      InputProps={{
        ...InputProps,
        endAdornment: <InputAdornment position="end">%</InputAdornment>
      }}
      helperText={helperText ?? derivedHours}
    />
  );
};
